import {
	customerPortalActorTable,
	customerPortalUserProjectTable,
	linearIssueLabelTable,
	linearIssueTable,
	linearLabelTable,
	linearProjectMilestoneTable,
	linearUserTable,
	linearWorkflowStateTable,
} from "@mojo/db/schema";
import { createServerFn } from "@tanstack/react-start";
import { getRequestHeaders } from "@tanstack/react-start/server";
import { and, count, desc, eq, inArray, isNull } from "drizzle-orm";
import { auth } from "@/lib/auth";
import { db } from "@/lib/db";

// Issue type derived from labels
export type IssueType = "bug" | "improvement" | "feature";

// Priority label mapped from numeric priority
export type IssuePriority = "urgent" | "high" | "medium" | "low" | "none";

export type IssueWithDetails = {
	id: string;
	identifier: string;
	title: string;
	description: string | null;
	priority: number;
	priorityLabel: IssuePriority;
	type: IssueType;
	url: string;
	createdAt: Date;
	updatedAt: Date;
	completedAt: Date | null;
	estimate: number | null;
	state: {
		id: string;
		name: string;
		type: string;
		color: string;
	} | null;
	labels: Array<{
		id: string;
		name: string;
		color: string;
	}>;
	assignee: {
		id: string;
		name: string;
		avatarUrl: string | null;
	} | null;
	actor: {
		id: string;
		name: string;
		color: string;
	} | null;
	milestone: {
		id: string;
		name: string;
	} | null;
};

export type Actor = {
	id: string;
	name: string;
	color: string;
	linearLabelId: string | null;
};

export type Milestone = {
	id: string;
	name: string;
	projectId: string;
	targetDate: string | null;
};

// Helper to map numeric priority to label
function mapPriorityToLabel(priority: number): IssuePriority {
	const map: Record<number, IssuePriority> = {
		1: "urgent",
		2: "high",
		3: "medium",
		4: "low",
		0: "none",
	};
	return map[priority] || "none";
}

// Helper to derive issue type from labels
function deriveIssueType(labels: Array<{ name: string }>): IssueType {
	for (const label of labels) {
		const name = label.name.toLowerCase();
		if (name.includes("bug")) return "bug";
		if (name.includes("improvement")) return "improvement";
		if (name.includes("feature")) return "feature";
	}
	return "feature"; // default
}

// Helper to get user's project IDs
async function getUserProjectIds(userId: string): Promise<string[]> {
	const userProjects = await db
		.select({ projectId: customerPortalUserProjectTable.projectId })
		.from(customerPortalUserProjectTable)
		.where(eq(customerPortalUserProjectTable.userId, userId));
	return userProjects.map((p) => p.projectId);
}

// Get all portal actors
export const getActors = createServerFn({ method: "GET" }).handler(
	async (): Promise<Actor[]> => {
		const actors = await db
			.select({
				id: customerPortalActorTable.id,
				name: customerPortalActorTable.name,
				color: customerPortalActorTable.color,
				linearLabelId: customerPortalActorTable.linearLabelId,
			})
			.from(customerPortalActorTable);

		return actors;
	},
);

// Get all milestones for user's projects
export const getMilestones = createServerFn({ method: "GET" }).handler(
	async (): Promise<Milestone[]> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return [];
		}

		const projectIds = await getUserProjectIds(session.user.id);

		if (projectIds.length === 0) {
			return [];
		}

		const milestones = await db
			.select({
				id: linearProjectMilestoneTable.id,
				name: linearProjectMilestoneTable.name,
				projectId: linearProjectMilestoneTable.projectId,
				targetDate: linearProjectMilestoneTable.targetDate,
			})
			.from(linearProjectMilestoneTable)
			.where(inArray(linearProjectMilestoneTable.projectId, projectIds))
			.orderBy(linearProjectMilestoneTable.targetDate);

		return milestones;
	},
);

// Get all issues for the current user's projects
export const getIssues = createServerFn({ method: "GET" }).handler(
	async (): Promise<{
		issues: IssueWithDetails[];
		total: number;
		openCount: number;
		actors: Actor[];
	}> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return { issues: [], total: 0, openCount: 0, actors: [] };
		}

		const projectIds = await getUserProjectIds(session.user.id);

		if (projectIds.length === 0) {
			return { issues: [], total: 0, openCount: 0, actors: [] };
		}

		// Build conditions
		const conditions = [
			inArray(linearIssueTable.projectId, projectIds),
			isNull(linearIssueTable.archivedAt),
		];

		// Get total count
		const [{ total }] = await db
			.select({ total: count() })
			.from(linearIssueTable)
			.where(and(...conditions));

		// Get issues with state, assignee, and milestone
		const issues = await db
			.select({
				id: linearIssueTable.id,
				identifier: linearIssueTable.identifier,
				title: linearIssueTable.title,
				description: linearIssueTable.description,
				priority: linearIssueTable.priority,
				estimate: linearIssueTable.estimate,
				url: linearIssueTable.url,
				createdAt: linearIssueTable.createdAt,
				updatedAt: linearIssueTable.linearUpdatedAt,
				completedAt: linearIssueTable.completedAt,
				// State
				stateId: linearWorkflowStateTable.id,
				stateName: linearWorkflowStateTable.name,
				stateType: linearWorkflowStateTable.type,
				stateColor: linearWorkflowStateTable.color,
				// Assignee
				assigneeId: linearUserTable.id,
				assigneeName: linearUserTable.name,
				assigneeAvatarUrl: linearUserTable.avatarUrl,
				// Milestone
				milestoneId: linearProjectMilestoneTable.id,
				milestoneName: linearProjectMilestoneTable.name,
			})
			.from(linearIssueTable)
			.leftJoin(
				linearWorkflowStateTable,
				eq(linearIssueTable.stateId, linearWorkflowStateTable.id),
			)
			.leftJoin(
				linearUserTable,
				eq(linearIssueTable.assigneeId, linearUserTable.id),
			)
			.leftJoin(
				linearProjectMilestoneTable,
				eq(linearIssueTable.projectMilestoneId, linearProjectMilestoneTable.id),
			)
			.where(and(...conditions))
			.orderBy(desc(linearIssueTable.linearUpdatedAt))
			.limit(100);

		// Get labels for all issues
		const issueIds = issues.map((i) => i.id);
		const issueLabels =
			issueIds.length > 0
				? await db
						.select({
							issueId: linearIssueLabelTable.issueId,
							labelId: linearLabelTable.id,
							labelName: linearLabelTable.name,
							labelColor: linearLabelTable.color,
						})
						.from(linearIssueLabelTable)
						.innerJoin(
							linearLabelTable,
							eq(linearIssueLabelTable.labelId, linearLabelTable.id),
						)
						.where(inArray(linearIssueLabelTable.issueId, issueIds))
				: [];

		// Get portal actors that have labels on the user's issues
		const actors = await db
			.selectDistinct({
				id: customerPortalActorTable.id,
				name: customerPortalActorTable.name,
				color: customerPortalActorTable.color,
				linearLabelId: customerPortalActorTable.linearLabelId,
			})
			.from(customerPortalActorTable)
			.innerJoin(
				linearLabelTable,
				eq(customerPortalActorTable.linearLabelId, linearLabelTable.id),
			)
			.innerJoin(
				linearIssueLabelTable,
				eq(linearLabelTable.id, linearIssueLabelTable.labelId),
			)
			.innerJoin(
				linearIssueTable,
				eq(linearIssueLabelTable.issueId, linearIssueTable.id),
			)
			.where(
				and(
					inArray(linearIssueTable.projectId, projectIds),
					isNull(linearIssueTable.archivedAt),
				),
			);

		// Build lookup map for actor by label ID
		const actorByLabelId = new Map(
			actors
				.filter((a) => a.linearLabelId !== null)
				.map((a) => [a.linearLabelId, a]),
		);

		// Count open issues (not completed or canceled)
		let openCount = 0;
		for (const issue of issues) {
			if (issue.stateType !== "completed" && issue.stateType !== "canceled") {
				openCount++;
			}
		}

		// Build result
		const issuesWithDetails: IssueWithDetails[] = issues.map((issue) => {
			const labels = issueLabels
				.filter((l) => l.issueId === issue.id)
				.map((l) => ({
					id: l.labelId,
					name: l.labelName,
					color: l.labelColor,
				}));

			// Find actor by matching label IDs
			let actor: IssueWithDetails["actor"] = null;
			for (const label of issueLabels.filter((l) => l.issueId === issue.id)) {
				const matchedActor = actorByLabelId.get(label.labelId);
				if (matchedActor) {
					actor = {
						id: matchedActor.id,
						name: matchedActor.name,
						color: matchedActor.color,
					};
					break;
				}
			}

			return {
				id: issue.id,
				identifier: issue.identifier,
				title: issue.title,
				description: issue.description,
				priority: issue.priority,
				priorityLabel: mapPriorityToLabel(issue.priority),
				type: deriveIssueType(labels),
				estimate: issue.estimate,
				url: issue.url,
				createdAt: issue.createdAt,
				updatedAt: issue.updatedAt,
				completedAt: issue.completedAt,
				state:
					issue.stateId &&
					issue.stateName &&
					issue.stateType &&
					issue.stateColor
						? {
								id: issue.stateId,
								name: issue.stateName,
								type: issue.stateType,
								color: issue.stateColor,
							}
						: null,
				labels,
				assignee:
					issue.assigneeId && issue.assigneeName
						? {
								id: issue.assigneeId,
								name: issue.assigneeName,
								avatarUrl: issue.assigneeAvatarUrl,
							}
						: null,
				actor,
				milestone:
					issue.milestoneId && issue.milestoneName
						? {
								id: issue.milestoneId,
								name: issue.milestoneName,
							}
						: null,
			};
		});

		return { issues: issuesWithDetails, total, openCount, actors };
	},
);
